/**
 * Hook for managing resizable panel width
 * Handles drag-to-resize and localStorage persistence
 */

import { useCallback, useEffect, useState } from 'react';

const DEFAULT_WIDTH = 900;
const MIN_WIDTH = 300;
const MAX_WIDTH_PERCENT = 0.8;

export function usePanelResize(storageKey = 'smartPanelWidth') {
  const [rightPanelWidth, setRightPanelWidth] = useState(() => {
    const saved = localStorage.getItem(storageKey);
    return saved ? Number.parseInt(saved, 10) : DEFAULT_WIDTH;
  });
  const [isResizing, setIsResizing] = useState(false);

  // Save to localStorage when width changes
  useEffect(() => {
    localStorage.setItem(storageKey, rightPanelWidth.toString());
  }, [rightPanelWidth, storageKey]);

  // Handle resize start
  const handleMouseDown = useCallback(() => {
    setIsResizing(true);
  }, []);

  // Handle mouse move and mouse up for resizing
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;

      // Calculate new width from right edge of window
      const newWidth = window.innerWidth - e.clientX;

      // Set min/max constraints
      const maxWidth = window.innerWidth * MAX_WIDTH_PERCENT;

      if (newWidth >= MIN_WIDTH && newWidth <= maxWidth) {
        setRightPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      // document.body can be null when this cleanup runs during page teardown
      // (React flushes passive-effect cleanups while unmounting). Guard against
      // it so the cleanup never throws and crashes the app.
      if (document.body) {
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }
    };
  }, [isResizing]);

  return {
    rightPanelWidth,
    setRightPanelWidth,
    isResizing,
    handleMouseDown,
  };
}
